import React, { useCallback, useEffect, useState } from 'react';

// 콘텐츠 종류별 편집 필드 정의 — 여기만 고치면 화면·저장이 같이 따라간다
const KINDS = {
  media: {
    label: '홍보자료 영상',
    fields: [
      ['GROUP_IDX', '그룹', 'group'],
      ['VIDEO_ID', '유튜브 ID', 'text', 'youtu.be/ 뒤의 문자열. 썸네일이 여기서 만들어진다'],
      ['URL', '영상 주소', 'text'],
      ['TITLE', '제목', 'text'],
      ['TITLE_EN', '제목 (English)', 'text'],
      ['SORT', '순서', 'num'],
      ['IS_ACTIVE', '노출', 'bool'],
    ],
    cols: ['TITLE', 'VIDEO_ID', 'SORT'],
  },
  mediagroup: {
    label: '홍보자료 그룹',
    fields: [
      ['NAME', '그룹명', 'text'], ['NAME_EN', '그룹명 (English)', 'text'],
      ['LEDE', '설명', 'text'], ['LEDE_EN', '설명 (English)', 'text'],
      ['SORT', '순서', 'num'], ['IS_ACTIVE', '노출', 'bool'],
    ],
    cols: ['NAME', 'SORT'],
  },
  faq: {
    label: '자주 묻는 질문',
    fields: [
      ['CATEGORY', '분류', 'text', '같은 값끼리 묶여서 표시된다'],
      ['CATEGORY_EN', '분류 (English)', 'text'],
      ['QUESTION', '질문', 'text'], ['QUESTION_EN', '질문 (English)', 'text'],
      ['ANSWER', '답변', 'area'], ['ANSWER_EN', '답변 (English)', 'area'],
      ['SORT', '순서', 'num'], ['IS_ACTIVE', '노출', 'bool'],
    ],
    cols: ['CATEGORY', 'QUESTION', 'SORT'],
  },
  history: {
    label: '연혁',
    fields: [
      ['YEAR', '연도', 'num'],
      ['CONTENT', '내용', 'text', '<strong>굵게</strong> 같은 간단한 태그를 쓸 수 있다'],
      ['CONTENT_EN', '내용 (English)', 'text'],
      ['SORT', '순서', 'num', '같은 해 안에서의 순서. 작을수록 위'],
      ['IS_ACTIVE', '노출', 'bool'],
    ],
    cols: ['YEAR', 'CONTENT', 'SORT'],
  },
};

const TABS = [['media', '홍보자료'], ['mediagroup', '영상 그룹'], ['faq', 'FAQ'],
              ['history', '연혁'], ['config', '사이트 설정']];

function AdminContent({ token }) {
  const [kind, setKind] = useState('media');
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [edit, setEdit] = useState(null);      // null | {} (신규) | row
  const [msg, setMsg] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };

  const load = useCallback(() => {
    fetch(`/api/admin/site/${kind}`, { headers: { Authorization: 'Bearer ' + token } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
    if (kind === 'media') {
      fetch('/api/admin/site/mediagroup', { headers: { Authorization: 'Bearer ' + token } })
        .then((r) => (r.ok ? r.json() : [])).then(setGroups).catch(() => {});
    }
  }, [kind, token]);

  useEffect(() => { setEdit(null); setMsg(''); load(); }, [load]);

  const save = (e) => {
    e.preventDefault();
    const idx = edit.IDX ? '/' + edit.IDX : '';
    fetch(`/api/admin/site/${kind}${idx}`, { method: 'POST', headers, body: JSON.stringify(edit) })
      .then((r) => r.json())
      .then((d) => {
        if (d && d.success) { setEdit(null); setMsg('저장했습니다.'); load(); }
        else setMsg('저장하지 못했습니다.');
      })
      .catch(() => setMsg('저장하지 못했습니다.'));
  };

  const remove = (row) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    fetch(`/api/admin/site/${kind}/${row.IDX}`, { method: 'DELETE', headers })
      .then(() => { setMsg('삭제했습니다.'); load(); });
  };

  // ── 사이트 설정 (키-값) ──────────────────
  if (kind === 'config') {
    const saveConfig = (e) => {
      e.preventDefault();
      fetch('/api/admin/site/config', { method: 'POST', headers, body: JSON.stringify({ items: rows }) })
        .then((r) => r.json())
        .then((d) => setMsg(d && d.success ? '저장했습니다.' : '저장하지 못했습니다.'));
    };
    return (
      <>
        <Tabs kind={kind} setKind={setKind} />
        <form className="ad-card" onSubmit={saveConfig}>
          <h3 className="ad-card-h">사이트 설정</h3>
          <p className="ad-card-sub">주소·연락처처럼 여러 화면에서 함께 쓰이는 값입니다.</p>
          {rows.map((r, i) => (
            <div className="admin-field" key={r.CFG_KEY}>
              <label>{r.LABEL} {r.HINT && <span className="ad-hint">{r.HINT}</span>}</label>
              <input value={r.VALUE || ''} onChange={(e) => {
                const n = rows.slice(); n[i] = { ...r, VALUE: e.target.value }; setRows(n);
              }} />
              <input className="ad-sub-input" placeholder="English" value={r.VALUE_EN || ''}
                     onChange={(e) => {
                       const n = rows.slice(); n[i] = { ...r, VALUE_EN: e.target.value }; setRows(n);
                     }} />
            </div>
          ))}
          <button className="admin-btn-primary" type="submit">저장</button>
          {msg && <div className="ad-msg ok">{msg}</div>}
        </form>
      </>
    );
  }

  const def = KINDS[kind];

  // ── 편집 폼 ──────────────────────────────
  if (edit) {
    return (
      <>
        <Tabs kind={kind} setKind={setKind} />
        <form className="ad-card" onSubmit={save}>
          <h3 className="ad-card-h">{edit.IDX ? `${def.label} 수정` : `${def.label} 추가`}</h3>
          {def.fields.map(([key, label, type, hint]) => (
            <div className="admin-field" key={key}>
              <label>{label} {hint && <span className="ad-hint">{hint}</span>}</label>
              {type === 'area' ? (
                <textarea rows="4" value={edit[key] || ''} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} />
              ) : type === 'bool' ? (
                <label className="ad-switch">
                  <input type="checkbox" checked={edit[key] !== 0}
                         onChange={(e) => setEdit({ ...edit, [key]: e.target.checked ? 1 : 0 })} />
                  <span>{edit[key] !== 0 ? '노출' : '숨김'}</span>
                </label>
              ) : type === 'group' ? (
                <select value={edit[key] || ''} onChange={(e) => setEdit({ ...edit, [key]: Number(e.target.value) })}>
                  <option value="">선택</option>
                  {groups.map((g) => <option key={g.IDX} value={g.IDX}>{g.NAME}</option>)}
                </select>
              ) : (
                <input type={type === 'num' ? 'number' : 'text'} value={edit[key] === null || edit[key] === undefined ? '' : edit[key]}
                       onChange={(e) => setEdit({ ...edit, [key]: type === 'num' ? Number(e.target.value) : e.target.value })} />
              )}
            </div>
          ))}
          <div className="ad-form-actions">
            <button className="admin-btn-primary" type="submit">저장</button>
            <button className="admin-btn-cancel" type="button" onClick={() => setEdit(null)}>취소</button>
          </div>
        </form>
      </>
    );
  }

  // ── 목록 ────────────────────────────────
  return (
    <>
      <Tabs kind={kind} setKind={setKind} />
      <div className="ad-list-head">
        <span>{def.label} <b>{rows.length}</b>건</span>
        <button className="admin-btn-primary" onClick={() => setEdit({ IS_ACTIVE: 1, SORT: rows.length + 1 })}>추가</button>
      </div>
      {msg && <div className="ad-msg ok">{msg}</div>}
      <div className="ad-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {def.cols.map((c) => <th key={c}>{(def.fields.find((f) => f[0] === c) || [, c])[1]}</th>)}
              <th>노출</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={def.cols.length + 2} style={{ textAlign: 'center', padding: 36, color: '#888' }}>등록된 항목이 없습니다.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.IDX}>
                {def.cols.map((c) => (
                  <td key={c} className={c === 'TITLE' || c === 'QUESTION' || c === 'CONTENT' ? 'admin-td-title' : ''}>
                    {c === 'CONTENT'
                      ? <span dangerouslySetInnerHTML={{ __html: String(r[c] || '').slice(0, 70) }} />
                      : String(r[c] === null || r[c] === undefined ? '' : r[c]).slice(0, 70)}
                  </td>
                ))}
                <td>{r.IS_ACTIVE ? '노출' : <span style={{ color: '#999' }}>숨김</span>}</td>
                <td>
                  <button className="admin-btn-sm" onClick={() => setEdit(r)}>수정</button>
                  <button className="admin-btn-sm admin-btn-del" onClick={() => remove(r)}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Tabs({ kind, setKind }) {
  return (
    <div className="ad-subtabs">
      {TABS.map(([k, label]) => (
        <button key={k} type="button" className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{label}</button>
      ))}
    </div>
  );
}

export default AdminContent;
